import { Fragment, useReducer, useState } from 'react';

import { Button, DatePicker, Form as TextField } from '@rrexpress/core';

import { IClonedKitbirdDropdownOptionItem } from '@/modules/apps/model/kitbird/dropdown';

import FormField from '../form/form-field';

export type FilterSectionType = 'layout' | 'input';

export type FilterSection = {
  layout?: {
    direction: 'horizontal' | 'vertical',
    sections: FilterSection[],
  },
  input?: FilterComponent,
};

export type FilterComponent = {
  type: 'dropdown' | 'textfield' | 'queryfield' | 'search' | 'datepicker',
  name: string,
  label?: string,
  defaultValue?: string | string[] | any,
  comboboxOptions?: { label: string, value: any }[],
  comboboxResource?: string,
  queryfieldKeyOptionLabel?: string;
  queryfieldKeyOptionValue?: string;
  queryfieldKeyQueryFilter?: string | string[];
  dataProviderName?: string;
};

export type ListFilterSubmitFn = (data: Record<string, any>) => Record<string, any>;

export type ListFilterOption = {
  // onSubmit purpose: modify data filter and must return new data filter,
  // new data filter will be used as new filter to hit API
  onSubmit?: ListFilterSubmitFn,
  sections: FilterSection[],
};

function getComponentsFromSections(sections: FilterSection[]) {
  const components: FilterComponent[] = [];

  sections.forEach((section) => {
    if (section.layout) {
      return getComponentsFromSections(section.layout.sections)
        .forEach((component) => components.push(component));
    }

    if (section.input) return components.push(section.input);

    return null;
  });

  return components;
}

function createInitialFormState(sections: FilterSection[]) {
  const components: FilterComponent[] = getComponentsFromSections(sections);

  const initialState: { [key: string]: any } = {};
  const exclude: string[] = [];

  components.forEach((component) => {
    if (exclude.includes(component.type)) return;

    switch (component.type) {
      case 'textfield':
      case 'queryfield':
        initialState[component.name] = '';
        break;
      case 'dropdown':
      case 'datepicker':
        initialState[component.name] = null;
        break;
      default:
        initialState[component.name] = component.defaultValue || '';
    }
  });

  return initialState;
}

const Filters = (props: ListFilterOption) => {
  const [state, dispatch] = useReducer(
    (current: any, action: any) => {
      if (action.name == null) return current;
      if (action.name === 'reset') {
        const initialState = createInitialFormState(props.sections || []);
        props?.onSubmit?.(initialState); // trigger reset
        return initialState;
      }

      return { ...current, [action.name]: action.value };
    },
    createInitialFormState(props.sections || []),
  );

  const [key, setKey] = useState(0); // trigger render when reset state (query field)

  const renderComponents = (component: FilterComponent) => {
    switch (component.type) {
      case 'textfield':
        return (
          <TextField
            name={component.name}
            label={component.label}
            value={state[component.name] || component.defaultValue || ''}
            onChange={(e) => dispatch({ name: component.name, value: e.target.value })}
          />
        );
      case 'dropdown':
        return (
          <FormField
            component="dropdown"
            id={component.name}
            name={component.name}
            value={state[component.name] || component.defaultValue || ''}
            onChange={(value) => {
              if (value === 'reset') {
                dispatch({ name: component.name, value: null });
              } else {
                dispatch({ name: component.name, value });
              }
            }}
            componentProps={{
              id: component.name,
              label: component.label,
              options: [
                {
                  label: '',
                  value: 'reset',
                },
                ...component.comboboxOptions as IClonedKitbirdDropdownOptionItem[],
              ],
              onSelectedOutputKey: 'value',
            }}
          />
        );
      case 'queryfield':
        return (
          <FormField
            key={component.name + key}
            id={component.name}
            component="queryfield"
            name={component.name}
            value={state[component.name] || ''}
            onChange={(value) => {
              if (value === 'reset') {
                dispatch({ name: component.name, value: '' });
              } else {
                dispatch({ name: component.name, value });
              }
            }
            }
            componentProps={{
              label: component.label,
              endpoint: component.comboboxResource as string,
              keyOptionLabel: component.queryfieldKeyOptionLabel as string,
              keyOptionValue: component.queryfieldKeyOptionValue as string,
              onSelectedOutputKey: 'value',
              keyQueryFilter: component.queryfieldKeyQueryFilter,
              dataProviderName: component.dataProviderName,
              defaultOptions: [{
                label: '',
                value: 'reset',
              }],
            }}
          />
        );
      case 'datepicker':
        return (
          <DatePicker
            value={state[component.name] || component.defaultValue || null}
            variant="default"
            onSubmit={() => null}
            onReset={() => dispatch({ name: component.name, value: null })}
            label={(component.label || null) as string}
            onChange={(value) => dispatch({ name: component.name, value })}
            footerNode={<></>}
          />
        );
      default:
        return <></>;
    }
  };

  const renderSections = (sections: FilterSection[]) => sections.map((section, i) => {
    if (section.layout) {
      if (section.layout.direction === 'vertical') {
        return (
          <div key={i} className="flex flex-col gap-y-4">
            {renderSections(section.layout.sections)}
          </div>
        );
      }
      return (
        <div key={i} className="flex items-center gap-4">
          {renderSections(section.layout.sections)}
        </div>
      );
    }

    if (section.input) {
      return (
        <Fragment key={i}>
          {renderComponents(section.input)}
        </Fragment>
      );
    }

    return null;
  }).filter((val) => val != null);

  function handleOnResetFilter() {
    dispatch({ name: 'reset', value: 'true' });
    setKey((prevKey) => prevKey + 1); // Force re-render by updating key
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();

        props?.onSubmit?.(state);
      }}
    >
      <div className="flex gap-4 mb-4">
        {renderSections(props.sections)}
      </div>
      <div className="flex gap-2">
        <Button type="submit">Tampilkan</Button>
        <Button type="button" variant="secondary" onClick={() => handleOnResetFilter()}>Reset</Button>
      </div>
    </form>
  );
};

export default Filters;
